import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useVehicleImage } from '../hooks/useVehicleImage';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { AyvensQuestion } from '../components/ayvens/AyvensQuestion';
import {
  Car, ArrowLeft, Fuel, Settings2, Calendar,
  ShieldCheck, Tag, FileDown,
  Loader2, Plus, BookUser, ImageIcon, Percent
} from 'lucide-react';
import { TIPOLOGIE_CLIENTE } from '../lib/tipologieCliente';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// Hero Image per dettaglio Offerta del Mese (link manuale prioritario)
const PromoDetailHero = ({ linkImmagine, marca, modello }) => {
  const { imageUrl: driveUrl } = useVehicleImage(marca, linkImmagine ? null : modello);
  const finalUrl = linkImmagine || driveUrl;
  if (!finalUrl) return null;
  const hdUrl = finalUrl.includes('drive.google.com') ? finalUrl.replace('sz=w400', 'sz=w1200') : finalUrl;
  return (
    <div className="w-full bg-gradient-to-b from-white to-amber-50/30 flex flex-col items-center justify-center px-8 py-6">
      <img src={hdUrl} alt={`${marca} ${modello}`} className="max-w-full max-h-48 object-contain drop-shadow-lg" loading="lazy" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
      <p className="text-[10px] text-slate-400 italic mt-2 text-center">
        L'immagine è puramente indicativa e può non rappresentare l'allestimento o il colore esatto del veicolo offerto
      </p>
    </div>
  );
};

const OffertaMeseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader, impersonatedUser } = useAuth();
  const [offerta, setOfferta] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedKm, setSelectedKm] = useState(null);
  const [anticipo, setAnticipo] = useState(0);

  // Contact state - allineato al catalogo standard
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactTipo, setNewContactTipo] = useState('smart');
  const [newContactAyvens, setNewContactAyvens] = useState(null);
  const [newContactData, setNewContactData] = useState({
    nome_cognome: '', telefono: '', email: '', citta: '', responsabile: '',
    tipologia_cliente: '', codice_fiscale: '', partita_iva: '',
    indirizzo: '', cap: '', km_annui_previsti: ''
  });
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  const fetchOfferta = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/offerte-mese/${id}`, { headers: getAuthHeader() });
      setOfferta(response.data);
    } catch (error) {
      toast.error('Offerta non trovata');
      navigate('/dashboard/offerte-mese');
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeader, navigate]);

  const fetchContacts = useCallback(async () => {
    try {
      const params = impersonatedUser ? `?view_as=${impersonatedUser.id}` : '';
      const response = await axios.get(`${API}/contacts${params}`, { headers: getAuthHeader() });
      setContacts(Array.isArray(response.data) ? response.data : response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, [getAuthHeader, impersonatedUser]);

  useEffect(() => {
    fetchOfferta();
  }, [fetchOfferta]);

  useEffect(() => {
    if (showContactDialog) fetchContacts();
  }, [showContactDialog, fetchContacts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (!offerta) return null;

  const durations = offerta.custom_durations?.length > 0 ? offerta.custom_durations : ['36', '48', '60'];
  const kmOptions = offerta.custom_km_options?.length > 0 ? offerta.custom_km_options : ['10000', '15000', '20000', '25000'];
  const quotations = offerta.quotations || {};

  const selectedKey = selectedDuration && selectedKm ? `${selectedDuration}_${selectedKm}` : null;
  const selectedCanone = selectedKey ? quotations[selectedKey] : null;

  let durataNumerica = 48;
  try { durataNumerica = parseInt(selectedDuration) || 48; } catch { /* fallback */ }
  const scontoAnticipo = anticipo > 0 && durataNumerica > 0 ? Math.ceil(anticipo / durataNumerica) : 0;
  const canoneFinalDisplay = selectedCanone ? selectedCanone - scontoAnticipo : null;

  const provvigioneFissa = offerta.provvigione_fissa || 0;
  // Formula gerarchica provvigioni (stessa del Catalogo):
  // 1) Reparto riceve commission_split% della provvigione totale
  // 2) TOP_AGENT/ADMIN prendono l'intera quota reparto
  // 3) AGENTE riceve competenza% della quota reparto
  const repartoCommissionSplit = impersonatedUser?.reparto_commission_split || user?.reparto_commission_split || 100;
  const provvigioneReparto = provvigioneFissa * repartoCommissionSplit / 100;
  const competenzaAgente = impersonatedUser?.competenza_provvigioni ?? user?.competenza_provvigioni ?? 100;
  const ruoloOperativo = impersonatedUser?.role || user?.role;
  const isRuoloApicale = ['top_agent', 'admin'].includes(ruoloOperativo);
  const provvigioneAgente = Math.round((isRuoloApicale ? provvigioneReparto : (provvigioneReparto * competenzaAgente / 100)) * 100) / 100;

  const handleGenerateOffer = () => {
    if (!selectedCanone) {
      toast.error('Seleziona una combinazione durata/km');
      return;
    }
    // Reset contact state
    setSelectedContactId('');
    setShowNewContactForm(false);
    setNewContactTipo('smart');
    setNewContactData({
      nome_cognome: '', telefono: '', email: '', citta: '', responsabile: '',
      tipologia_cliente: '', codice_fiscale: '', partita_iva: '',
      indirizzo: '', cap: '', km_annui_previsti: ''
    });
    setShowContactDialog(true);
  };

  const handleCreateNewContact = async () => {
    if (newContactAyvens === null) {
      toast.error('Indica se il cliente è già cliente Ayvens / ALD Automotive');
      throw new Error('ayvens-required');
    }
    const payload = {
      gia_cliente_ayvens: newContactAyvens,
      tipo_profilo: newContactTipo,
      nome_cognome: newContactData.nome_cognome,
      telefono: newContactData.telefono,
      email: newContactData.email,
      citta: newContactData.citta || null,
      responsabile: newContactData.responsabile || null,
      tipologia_cliente: newContactData.tipologia_cliente || null,
      codice_fiscale: newContactData.codice_fiscale || null,
      partita_iva: newContactData.partita_iva || null,
      indirizzo: newContactData.indirizzo || null,
      cap: newContactData.cap || null,
      km_annui_previsti: newContactData.km_annui_previsti ? parseInt(newContactData.km_annui_previsti) : null
    };

    try {
      const response = await axios.post(`${API}/contacts`, payload, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      toast.success('Contatto creato!');
      await fetchContacts();
      setSelectedContactId(response.data.id);
      setShowNewContactForm(false);
      return response.data.id;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione contatto');
      throw error;
    }
  };

  const handleConfirmGenerate = async () => {
    let contactId = selectedContactId;

    if (showNewContactForm) {
      try {
        contactId = await handleCreateNewContact();
      } catch {
        return;
      }
    }

    if (!contactId) {
      toast.error('Seleziona o crea un cliente');
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        offerta_mese_id: offerta.id,
        durata_label: selectedDuration,
        km_label: selectedKm,
        contact_id: contactId,
        anticipo: anticipo,
        notes: notes || null
      };
      // Determina l'agente "effettivo" a cui assegnare l'offerta:
      // 1) Impersonificazione esplicita dalla topbar -> usa quella
      // 2) Altrimenti, se l'utente attuale (SuperAdmin/Admin/Top Agent) sta creando
      //    un'offerta per un contatto di un altro agente -> usa l'owner_id del contatto
      const canImpersonate = ['super_admin', 'admin', 'top_agent'].includes(user?.role);
      if (impersonatedUser) {
        payload.impersonate_user_id = impersonatedUser.id;
      } else if (canImpersonate) {
        const selectedContact = contacts.find(c => c.id === contactId);
        const contactOwnerId = selectedContact?.owner_id;
        if (contactOwnerId && contactOwnerId !== user.id) {
          payload.impersonate_user_id = contactOwnerId;
        }
      }
      const response = await axios.post(`${API}/offerte-mese/genera-offerta`, payload, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });

      const offerId = response.data.id;
      toast.success(`Offerta ${response.data.codice_offerta} generata con successo!`);
      setShowContactDialog(false);

      try {
        const pdfRes = await axios.get(`${API}/offers/${offerId}/pdf`, {
          headers: getAuthHeader(),
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Offerta_${response.data.codice_offerta}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        // PDF download fallback
      }

      navigate('/dashboard/offerte');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione dell\'offerta');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="offerta-mese-detail">
      <div className="flex gap-2">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard/offerte-mese')}
          className="text-slate-600 hover:text-slate-800"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alle Offerte del Mese
        </Button>
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard/catalogo')}
          className="text-slate-600 hover:text-slate-800"
          data-testid="btn-torna-catalogo"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna al Catalogo
        </Button>
      </div>

      {/* Vehicle Info */}
      <Card className="border-2 border-amber-400">
        <PromoDetailHero linkImmagine={offerta.link_immagine_effective || offerta.link_immagine} marca={offerta.marca} modello={offerta.modello} />
        <CardHeader className="bg-gradient-to-br from-amber-50 to-amber-100/50 pb-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-medium text-amber-700">{offerta.marca}</p>
                <Badge className="bg-amber-500 text-white text-xs">PROMO</Badge>
                {offerta.company && (
                  <Badge variant="outline" className="border-amber-400 text-amber-700 text-xs">{offerta.company}</Badge>
                )}
              </div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                {offerta.modello}
              </CardTitle>
              {offerta.versione && <p className="text-slate-600 mt-1">{offerta.versione}</p>}
              {offerta.listino && (
                <Badge className="bg-slate-700 text-white text-xs mt-2">{offerta.listino}</Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {offerta.alimentazione && (
              <div>
                <p className="text-xs text-slate-500 flex items-center gap-1"><Fuel className="w-3 h-3" /> Alimentazione</p>
                <p className="text-sm font-medium">{offerta.alimentazione}</p>
              </div>
            )}
            {offerta.cambio && (
              <div>
                <p className="text-xs text-slate-500 flex items-center gap-1"><Settings2 className="w-3 h-3" /> Cambio</p>
                <p className="text-sm font-medium">{offerta.cambio}</p>
              </div>
            )}
            {offerta.colore && (
              <div>
                <p className="text-xs text-slate-500">Colore</p>
                <p className="text-sm font-medium">{offerta.colore}</p>
              </div>
            )}
            {offerta.data_disponibilita && (
              <div>
                <p className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Disponibilit{'\u00E0'}</p>
                <p className="text-sm font-medium">{offerta.data_disponibilita}</p>
              </div>
            )}
            {offerta.data_immatricolazione && (
              <div>
                <p className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Immatricolazione</p>
                <p className="text-sm font-medium">{offerta.data_immatricolazione}</p>
              </div>
            )}
          </div>

          {(offerta.franchigia_rca || offerta.franchigia_danni || offerta.franchigia_furto) && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Franchigie Assicurative
              </p>
              <div className="flex gap-4">
                {offerta.franchigia_rca && <Badge variant="outline" className="border-slate-300">RCA: {offerta.franchigia_rca}</Badge>}
                {offerta.franchigia_danni && <Badge variant="outline" className="border-slate-300">Danni: {offerta.franchigia_danni}</Badge>}
                {offerta.franchigia_furto && <Badge variant="outline" className="border-slate-300">Furto: {offerta.franchigia_furto}</Badge>}
              </div>
            </div>
          )}

          {offerta.note && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-slate-500 italic">{offerta.note}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing Matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            <Tag className="w-5 h-5 text-amber-500" />
            {selectedCanone
              ? `Canone selezionato: \u20AC${canoneFinalDisplay?.toLocaleString('it-IT')}/mese`
              : 'Seleziona durata e chilometraggio'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  <th className="p-3 text-left text-slate-600 bg-slate-50 border border-slate-200">Durata</th>
                  {kmOptions.map((km) => (
                    <th key={km} className="p-3 text-center text-slate-600 bg-slate-50 border border-slate-200">
                      {parseInt(km) ? `${parseInt(km).toLocaleString('it-IT')} km` : km}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {durations.map((durata) => (
                  <tr key={durata}>
                    <td className="p-3 font-medium text-slate-700 bg-slate-50 border border-slate-200">
                      {durata} mesi
                    </td>
                    {kmOptions.map((km) => {
                      const key = `${durata}_${km}`;
                      const canone = quotations[key];
                      const isAvailable = canone && canone > 0;
                      const isSelected = selectedDuration === durata && selectedKm === km;

                      return (
                        <td
                          key={key}
                          className={`p-3 text-center border border-slate-200 transition-all ${
                            isSelected
                              ? 'bg-amber-500 text-white font-bold ring-2 ring-amber-600'
                              : isAvailable
                                ? 'cursor-pointer hover:bg-amber-50 hover:border-amber-300 text-slate-800 font-medium'
                                : 'bg-slate-100 text-slate-300'
                          }`}
                          onClick={() => {
                            if (isAvailable) {
                              setSelectedDuration(durata);
                              setSelectedKm(km);
                            }
                          }}
                          data-testid={`cell-${durata}-${km}`}
                        >
                          {isAvailable ? <>{'\u20AC'}{canone.toLocaleString('it-IT')}</> : <span className="text-xs">-</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedCanone && (
            <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-600">Anticipo ({'\u20AC'})</Label>
                  <Input
                    type="number"
                    value={anticipo || ''}
                    onChange={e => setAnticipo(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="mt-1"
                    data-testid="input-anticipo"
                  />
                  {scontoAnticipo > 0 && (
                    <p className="text-xs text-slate-500 mt-1">Sconto: -{'\u20AC'}{scontoAnticipo}/mese</p>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-600">Canone Mensile</p>
                  <p className="text-3xl font-bold text-amber-600">
                    {'\u20AC'}{canoneFinalDisplay?.toLocaleString('it-IT')}
                  </p>
                  <p className="text-xs text-slate-500">
                    {selectedDuration} mesi / {parseInt(selectedKm) ? parseInt(selectedKm).toLocaleString('it-IT') : selectedKm} km
                  </p>
                </div>
                <div className="text-center sm:text-right">
                  <p className="text-xs text-slate-600 flex items-center gap-1 justify-center sm:justify-end">
                    {isSuperAdmin && <Percent className="w-3 h-3" />} Provvigione
                  </p>
                  {isSuperAdmin ? (
                    <p className="text-lg font-bold text-emerald-600">{'\u20AC'}{provvigioneFissa.toLocaleString('it-IT')}</p>
                  ) : (
                    <p className="text-lg font-bold text-emerald-600">{'\u20AC'}{provvigioneAgente.toLocaleString('it-IT')}</p>
                  )}
                </div>
              </div>

              <Button
                onClick={handleGenerateOffer}
                className="w-full h-14 text-base font-semibold bg-amber-500 hover:bg-amber-600"
                data-testid="btn-genera-offerta"
              >
                <FileDown className="w-5 h-5 mr-2" />
                Genera Offerta PDF
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== DIALOG SELEZIONE CLIENTE - Identico al Catalogo Standard ===== */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookUser className="w-5 h-5" />
              Genera Offerta - Selezione Cliente
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Toggle: Rubrica / Nuovo Contatto */}
            <div className="flex gap-2">
              <Button
                variant={!showNewContactForm ? "default" : "outline"}
                className="flex-1"
                onClick={() => setShowNewContactForm(false)}
                data-testid="btn-rubrica-tab"
              >
                Seleziona dalla Rubrica
              </Button>
              <Button
                variant={showNewContactForm ? "default" : "outline"}
                className="flex-1"
                onClick={() => setShowNewContactForm(true)}
                data-testid="btn-nuovo-contatto-tab"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nuovo Contatto
              </Button>
            </div>

            {!showNewContactForm ? (
              /* ===== SELEZIONE DA RUBRICA ===== */
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Seleziona Contatto *</Label>
                  <Select value={selectedContactId} onValueChange={setSelectedContactId}>
                    <SelectTrigger data-testid="contact-select">
                      <SelectValue placeholder="Cerca nella rubrica..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...contacts]
                        .sort((a, b) => (a.nome_cognome || '').localeCompare(b.nome_cognome || '', 'it', { sensitivity: 'base' }))
                        .map(c => {
                          const initial = (c.nome_cognome || '?').trim().charAt(0).toUpperCase() || '?';
                          return (
                            <SelectItem key={c.id} value={c.id}>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-slate-100 text-slate-700 font-bold w-6 h-6 flex items-center justify-center p-0 rounded-full">
                                  {initial}
                                </Badge>
                                {c.nome_cognome} - {c.telefono}
                              </div>
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                </div>
                {selectedContactId && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    {(() => {
                      const contact = contacts.find(c => c.id === selectedContactId);
                      return contact ? (
                        <div className="text-sm">
                          <p className="font-medium">{contact.nome_cognome}</p>
                          <p className="text-slate-500">{contact.email} {contact.telefono && `\u2022 ${contact.telefono}`}</p>
                          {contact.citta && <p className="text-slate-500">{contact.citta}</p>}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
            ) : (
              /* ===== FORM NUOVO CONTATTO (Smart / Prospect) ===== */
              <div className="space-y-4">
                {/* Profile type toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium">Tipo Profilo</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${newContactTipo === 'smart' ? 'font-semibold text-blue-600' : 'text-slate-400'}`}>Smart</span>
                    <Switch
                      checked={newContactTipo === 'prospect'}
                      onCheckedChange={(checked) => setNewContactTipo(checked ? 'prospect' : 'smart')}
                      data-testid="toggle-tipo-profilo"
                    />
                    <span className={`text-xs ${newContactTipo === 'prospect' ? 'font-semibold text-violet-600' : 'text-slate-400'}`}>Prospect</span>
                  </div>
                </div>

                {/* Common fields */}
                <div className="space-y-2">
                  <Label>Nome e Cognome / Ragione Sociale *</Label>
                  <Input
                    value={newContactData.nome_cognome}
                    onChange={(e) => setNewContactData({ ...newContactData, nome_cognome: e.target.value })}
                    placeholder="Es. Mario Rossi"
                    data-testid="input-new-nome"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Telefono *</Label>
                    <Input
                      value={newContactData.telefono}
                      onChange={(e) => setNewContactData({ ...newContactData, telefono: e.target.value })}
                      placeholder="333 1234567"
                      data-testid="input-new-telefono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newContactData.email}
                      onChange={(e) => setNewContactData({ ...newContactData, email: e.target.value })}
                      placeholder="email@esempio.it"
                      data-testid="input-new-email"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Citt{'\u00E0'} {newContactTipo === 'prospect' ? '*' : ''}</Label>
                    <Input
                      value={newContactData.citta}
                      onChange={(e) => setNewContactData({ ...newContactData, citta: e.target.value })}
                      placeholder="Roma"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Responsabile {newContactTipo === 'prospect' ? '*' : ''}</Label>
                    <Input
                      value={newContactData.responsabile}
                      onChange={(e) => setNewContactData({ ...newContactData, responsabile: e.target.value })}
                      placeholder="Nome referente"
                    />
                  </div>
                </div>

                {/* Prospect-specific fields */}
                {newContactTipo === 'prospect' && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-violet-600 font-medium mb-3">Campi Prospect</p>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Tipologia Cliente *</Label>
                        <Select value={newContactData.tipologia_cliente} onValueChange={(v) => setNewContactData({ ...newContactData, tipologia_cliente: v })}>
                          <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                          <SelectContent>
                            {TIPOLOGIE_CLIENTE.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Codice Fiscale</Label>
                          <Input
                            value={newContactData.codice_fiscale}
                            onChange={(e) => setNewContactData({ ...newContactData, codice_fiscale: e.target.value.toUpperCase() })}
                            placeholder="RSSMRA80A01H501Z"
                            maxLength={16}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Partita IVA</Label>
                          <Input
                            value={newContactData.partita_iva}
                            onChange={(e) => setNewContactData({ ...newContactData, partita_iva: e.target.value })}
                            placeholder="12345678901"
                            maxLength={11}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Indirizzo *</Label>
                        <Input
                          value={newContactData.indirizzo}
                          onChange={(e) => setNewContactData({ ...newContactData, indirizzo: e.target.value })}
                          placeholder="Via Roma 123"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>CAP *</Label>
                          <Input
                            value={newContactData.cap}
                            onChange={(e) => setNewContactData({ ...newContactData, cap: e.target.value })}
                            placeholder="00100"
                            maxLength={5}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Km annui previsti *</Label>
                          <Input
                            type="number"
                            value={newContactData.km_annui_previsti}
                            onChange={(e) => setNewContactData({ ...newContactData, km_annui_previsti: e.target.value })}
                            placeholder="15000"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {showNewContactForm && (
              <AyvensQuestion value={newContactAyvens} onChange={setNewContactAyvens} variant="staff" testIdPrefix="new-contact-ayvens" />
            )}

            {/* Note Offerta */}
            <div className="space-y-2 pt-2 border-t">
              <Label>Note Offerta</Label>
              <Textarea
                placeholder="Note aggiuntive..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="offer-notes-input"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowContactDialog(false)}>
                Annulla
              </Button>
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                onClick={handleConfirmGenerate}
                disabled={generating || (!showNewContactForm && !selectedContactId)}
                data-testid="confirm-offer-btn"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generazione...</>
                ) : (
                  <><FileDown className="w-4 h-4 mr-2" /> Crea e Scarica PDF</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OffertaMeseDetailPage;
